"use client";

import { ROUTES } from "@/constants/routes";
import { useCurrentUser } from "@/hooks/user";
import { UserRole } from "@prisma/client";
import PollPollE from "@public/svgs/poll-poll-e.svg";
import { Typo } from "@repo/ui/components";
import {
  BrainIcon,
  ClipboardListIcon,
  ExternalLinkIcon,
  GamepadIcon,
  SettingsIcon,
  XIcon,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

export function AdminToolbar() {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const { data: currentUser } = useCurrentUser();

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const handleAdminSelect = useCallback(
    (category: "RESEARCH" | "TEST" | "QUIZ") => {
      if (category === "QUIZ") {
        router.push(ROUTES.EDITOR_QUIZ_CREATE);
      } else {
        router.push(`${ROUTES.CREATE_MISSION}?category=${category}`);
      }
      setIsOpen(false);
    },
    [router],
  );

  const isAdmin = currentUser?.role === UserRole.ADMIN;

  if (!hasMounted || !isAdmin) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className="fixed bottom-24 right-4 z-9999 flex h-12 items-center justify-center gap-2 rounded-full bg-zinc-800 px-4 text-white shadow-lg transition-colors hover:bg-zinc-700 active:bg-zinc-900"
        aria-label="관리자 도구 열기"
      >
        {isOpen ? (
          <XIcon className="size-5" />
        ) : (
          <>
            <PollPollE className="size-5" />
            <span className="text-sm font-medium">Admin</span>
          </>
        )}
      </button>

      {isOpen && (
        <div className="fixed bottom-40 right-4 z-9999 w-72 overflow-hidden rounded-xl border border-default bg-white text-default shadow-2xl">
          <div className="flex items-center justify-between bg-zinc-800 px-4 py-3">
            <div className="flex items-center gap-2">
              <PollPollE className="size-5 text-white" />
              <Typo.SubTitle size="large" className="text-white">
                Admin Tools
              </Typo.SubTitle>
            </div>
          </div>

          <div className="flex flex-col">
            <Link
              href="/admin/v2/"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-default transition-colors hover:bg-zinc-50"
              onClick={() => setIsOpen(false)}
            >
              <SettingsIcon className="size-4 text-zinc-500" />
              관리자 페이지
              <ExternalLinkIcon className="ml-auto size-3.5 text-zinc-400" />
            </Link>
          </div>

          <div className="h-px bg-zinc-100" />

          <button
            type="button"
            onClick={() => handleAdminSelect("RESEARCH")}
            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-default transition-colors hover:bg-zinc-50 justify-between w-full"
          >
            <ClipboardListIcon className="size-4 text-zinc-500" />
            설문조사/리서치 생성
            <ExternalLinkIcon className="ml-auto size-3.5 text-zinc-400" />
          </button>
          <button
            type="button"
            onClick={() => handleAdminSelect("TEST")}
            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-default transition-colors hover:bg-zinc-50 justify-between w-full"
          >
            <BrainIcon className="size-4 text-zinc-500" />
            심리/유형 테스트 생성
            <ExternalLinkIcon className="ml-auto size-3.5 text-zinc-400" />
          </button>
          <button
            type="button"
            onClick={() => handleAdminSelect("QUIZ")}
            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-default transition-colors hover:bg-zinc-50 justify-between w-full"
          >
            <GamepadIcon className="size-4 text-zinc-500" />
            퀴즈 생성
            <ExternalLinkIcon className="ml-auto size-3.5 text-zinc-400" />
          </button>
        </div>
      )}
    </>
  );
}
